import { Browser } from '@capacitor/browser';

import i18n from '../../app/i18n';
import { isRemix } from './ssrUtils';

export const getNormalisedHostname = (hostname: string) => {
  return hostname.replace(/^www\./, '');
};

export const isSameHostname = (a: URL, b: URL) => {
  return (
    getNormalisedHostname(a.hostname) === getNormalisedHostname(b.hostname)
  );
};

export const getCookieDomain = () => {
  if (isRemix()) {
    return undefined;
  }

  // microsites should use the main Sim Local Shop domain
  if (location.hostname.endsWith('shop.simlocal.com')) {
    return 'shop.simlocal.com';
  }

  // esimchoice.com subdomains should use the main domain
  if (location.hostname.endsWith('esimchoice.com')) {
    return 'esimchoice.com';
  }

  // non-prod shop subdomains should use the main domain
  const match = location.hostname.match(
    /shop\.(.+)\.(.+)\.esim\.travelwinnonproduction\.com$/,
  );
  if (match) {
    const brand = match[1];
    const env = match[2];

    return `shop.${brand}.${env}.esim.travelwinnonproduction.com`;
  }

  return getNormalisedHostname(location.hostname);
};

export const getPathnameWithoutLocale = (pathname: string) => {
  if (
    pathname === '/' ||
    i18n.supportedLngs.some((lang) => pathname === `/${lang}`)
  ) {
    return '/';
  }

  const firstSegment = pathname.split('/').at(1);

  if (firstSegment && i18n.supportedLngs.includes(firstSegment)) {
    return pathname.substring(`/${firstSegment}`.length);
  }

  return pathname;
};

export const getPathnameWithLocale = (
  pathname: string,
  lang: string | undefined,
) => {
  if (!lang || lang === 'en' || !i18n.supportedLngs.includes(lang)) {
    return pathname;
  }

  if (pathname === '/') {
    return `/${lang}`;
  }

  return `/${lang}${pathname.startsWith('/') ? pathname : `/${pathname}`}`;
};

export const openUrl = (url: string) => {
  if (url.startsWith('com.simlocal.esim.travel')) {
    // on ios the in-app browser has to be closed manually
    Browser.close();

    let parsedUrl = new URL(url);

    // (TF-597) Fix for Android: url.pathname will return origin/pathname if we don't do this
    if (parsedUrl.pathname.length > 1) {
      const urlWithoutCustomProtocol = url.replace(
        'com.simlocal.esim.travel',
        'https',
      );

      parsedUrl = new URL(urlWithoutCustomProtocol);
    }

    const newUrl = new URL(window.location.origin);
    newUrl.pathname = parsedUrl.pathname;
    newUrl.search = parsedUrl.search;

    // pass OAuth redirection params (e.g. `?code`) to a new location
    // that can be picked up by and parsed by @aws-amplify/auth
    window.location.href = newUrl.toString();
  }
};
